import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faTag, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import { deleteCategoryAndReassignNotes, removeUserTag, renameCategoryAndUpdateNotes, restoreCategoriesFromNotes } from '../firebase/notes';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { useTheme } from '../context/ThemeContext';
import { useEncryption } from '../context/EncryptionContext';

const availableColors = [
  'bg-gray-800', 'bg-blue-200', 'bg-red-500', 'bg-yellow-200', 'bg-blue-500', 
  'bg-gray-300', 'bg-purple-300', 'bg-green-200', 'bg-orange-200', 'bg-teal-200', 
  'bg-red-300', 'bg-yellow-300', 'bg-pink-300', 'bg-indigo-300', 'bg-gray-400', 
  'bg-lime-200'
];

const Sidebar = ({ categories, setCategories, onCategorySelect, notes, tags, setShowTagManager, onTagSelect, selectedCategory, selectedTag, onMobileSelection, className = '', id }) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedColor, setSelectedColor] = useState(availableColors[0]);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, tag: null });
  const contextMenuRef = useRef(null);
  const [user] = useAuthState(auth);
  const { theme, themes } = useTheme();
  const isLightTheme = themes[theme]?.mode === 'light';
  const { isEncryptionLocked } = useEncryption();
  const [isRestoring, setIsRestoring] = useState(false);

  const handleAddCategory = () => {
    if (newCategoryName.trim() === '') return;
    const newCategory = {
      name: newCategoryName,
      color: selectedColor,
    };
    const updatedCategories = [...categories.slice(0, categories.length - 1), newCategory, categories[categories.length - 1]];
    setCategories(updatedCategories);
    setNewCategoryName('');
    setSelectedColor(availableColors[0]);
  };

  const handleEditCategory = async (index) => {
    if (isEncryptionLocked) return;
    const previousCategoryName = categories[index].name;
    try {
      if (!user) {
        throw new Error('User must be signed in to rename a category');
      }

      await renameCategoryAndUpdateNotes(user.uid, previousCategoryName, {
        name: newCategoryName,
        color: selectedColor,
      });

      if (selectedCategory === previousCategoryName) {
        onCategorySelect(newCategoryName);
      }
      setIsEditing(false);
      setCategoryToEdit(null);
      setNewCategoryName('');
      setSelectedColor(availableColors[0]);
    } catch (error) {
      console.error('Error renaming category:', error);
      if (error.code === 'category/rename-limit') {
        alert(`Could not rename "${previousCategoryName}". Categories with more than 499 notes cannot be renamed atomically, so the category was kept.`);
      } else if (error.code === 'category/rename-conflict') {
        alert(`Could not rename "${previousCategoryName}" because it is no longer available. The category was kept.`);
      } else if (error.code === 'permission-denied') {
        alert(`Could not rename "${previousCategoryName}". Check that you are signed in and have permission. If you use email/password, verify your email and try again.`);
      } else {
        alert(`Could not rename "${previousCategoryName}". The category was kept; please try again.`);
      }
    }
  };

  const handleDeleteCategory = async (index) => {
    if (isEncryptionLocked) return;
    const categoryName = categories[index].name;
    if (categoryName === 'Uncategorized' || categoryName === 'All Notes') {
      alert(`The category "${categoryName}" cannot be deleted.`);
      return;
    }

    const notesInCategory = notes.filter(note => note.category === categoryName);
    const confirmDelete = window.confirm(
      `Delete the category "${categoryName}"? ` +
      `${notesInCategory.length} note${notesInCategory.length === 1 ? '' : 's'} will be moved to Uncategorized. ` +
      'No notes will be deleted.'
    );

    if (!confirmDelete) {
      return;
    }

    try {
      if (!user) {
        throw new Error('User must be signed in to delete a category');
      }

      const updatedCategories = categories.filter((_, i) => i !== index);
      await deleteCategoryAndReassignNotes(user.uid, notesInCategory, updatedCategories);

      if (selectedCategory === categoryName) {
        onCategorySelect('Uncategorized');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      if (error.code === 'category/reassignment-limit') {
        alert(`Could not delete "${categoryName}". Categories with more than 499 notes cannot yet be moved atomically, so the category was kept.`);
      } else {
        alert(`Could not delete "${categoryName}". The category was kept; please try again.`);
      }
    }
  };

  const startEditing = (category, index) => {
    setIsEditing(true);
    setCategoryToEdit(index);
    setNewCategoryName(category.name);
    setSelectedColor(category.color);
  };

  const handleDragStart = (e, tag) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', tag);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleTagRightClick = (e, tag) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      tag: tag
    });
  };

  const handleTagDelete = async (tag) => {
    if (isEncryptionLocked) return;
    if (user) {
      try {
        const success = await removeUserTag(user.uid, tag);
        if (success) {
          setContextMenu({ visible: false, x: 0, y: 0, tag: null });
          // If the deleted tag is currently selected, clear the selection
          if (selectedTag?.includes(tag)) {
            onTagSelect(tag);
          }
        }
      } catch (error) {
        console.error('Error removing tag:', error);
      }
    }
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, tag: null });
  };

  const selectCategory = (categoryName) => {
    onCategorySelect(categoryName);
    onMobileSelection?.();
  };

  const selectTag = (tag) => {
    onTagSelect(tag);
    onMobileSelection?.();
  };

  const activateWithKeyboard = (event, callback) => {
    if (event.currentTarget !== event.target || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    event.stopPropagation();
    callback();
  };

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        closeContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible]);

  return (
    <aside id={id} className={`w-full p-4 flex flex-col h-auto bg-transparent border-r md:w-64 md:h-full ${
      isLightTheme ? 'border-gray-400/40 text-gray-700' : 'border-blue-950/40 text-gray-300'
    } ${className}`}>
      <nav className="flex-1 min-h-0 overflow-y-auto">
        {isEncryptionLocked && <div role="status" className="p-2 text-sm text-gray-700 dark:text-gray-300">Note changes are temporarily unavailable while encryption is being verified or changed.</div>}
        {/* Categories Section */}
        <div className="category-section">
          <h2 className={`mb-2 px-2 text-xs font-semibold uppercase tracking-wider ${isLightTheme ? 'text-gray-600' : 'text-gray-300'}`}>
            Notes
          </h2>
          <div className="space-y-1">
            {categories.map((category, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                className={`flex items-center justify-between px-2 py-2 rounded group relative cursor-pointer ${
                  selectedCategory === category.name
                    ? isLightTheme
                      ? 'bg-blue-100 ring-2 ring-inset ring-blue-400 text-blue-800'
                      : 'bg-gray-600 ring-2 ring-inset ring-yellow-500 text-white'
                    : isLightTheme
                      ? 'text-gray-700 hover:bg-blue-50 hover:text-blue-800'
                      : 'text-gray-300 hover:bg-gray-600 hover:text-white'
                }`}
                onClick={() => selectCategory(category.name)}
                onKeyDown={(event) => activateWithKeyboard(event, () => selectCategory(category.name))}
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${category.color} mr-3`}></div>
                  <span>{category.name}</span>
                </div>
                {category.name !== 'Uncategorized' && category.name !== 'All Notes' && (
                  <div className="absolute right-2 opacity-0 group-hover:opacity-100 flex space-x-2">
                    <button type="button" data-testid={`edit-category-${category.name}`} className={`${isLightTheme ? 'text-gray-500' : 'text-gray-400'} hover:text-white`} onClick={(e) => { e.stopPropagation(); startEditing(category, index); }}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button type="button" data-testid={`delete-category-${category.name}`} disabled={isEncryptionLocked} className={`${isLightTheme ? 'text-gray-500' : 'text-gray-400'} hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed`} onClick={(e) => { e.stopPropagation(); handleDeleteCategory(index); }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={`mt-5 border-t pt-4 ${isLightTheme ? 'border-gray-400/30' : 'border-blue-950/30'}`}>
            <h3 className={`mb-2 px-1 text-xs font-semibold uppercase tracking-wider ${isLightTheme ? 'text-gray-600' : 'text-gray-300'}`}>
              {isEditing ? 'Edit category' : 'New category'}
            </h3>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={isEditing ? "Edit Category" : "New Category"}
              className={`p-2 mb-2 rounded border w-full ${
                isLightTheme
                  ? 'bg-white text-gray-700 border-gray-300' 
                  : 'bg-gray-700 text-white border-gray-300'
              }`}
            />
            <div className="mb-2 flex flex-wrap gap-1.5 px-2">
              {availableColors.map((color, index) => (
                <button
                  type="button"
                  key={index}
                  aria-label={`Select ${color.replace('bg-', '').replace('-', ' ')}`}
                  aria-pressed={selectedColor === color}
                  className={`h-5 w-5 rounded-full ${color} cursor-pointer transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedColor === color
                      ? isLightTheme
                        ? 'ring-2 ring-blue-700 ring-offset-2 ring-offset-white scale-110'
                        : 'ring-2 ring-yellow-300 ring-offset-2 ring-offset-gray-800 scale-110'
                      : isLightTheme
                        ? 'focus:ring-blue-700 focus:ring-offset-white'
                        : 'focus:ring-yellow-300 focus:ring-offset-gray-800'
                  }`}
                  onClick={() => setSelectedColor(color)}
                />
              ))}
            </div>
            <button
              onClick={isEditing ? () => handleEditCategory(categoryToEdit) : handleAddCategory}
              disabled={isEditing && isEncryptionLocked}
              className={`mt-2 block w-full p-2 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                isLightTheme
                  ? 'bg-blue-500 hover:bg-blue-400'
                  : 'bg-blue-700 hover:bg-blue-600'
              }`}
            >
              <FontAwesomeIcon icon={faPlus} /> {isEditing ? "Save Changes" : "Create Category"}
            </button>
          </div>
        </div>

        {/* Tags Section */}
        <div className={`tag-section mt-6 border-t pt-5 ${isLightTheme ? 'border-gray-400/30' : 'border-blue-950/30'}`}>
          <h3 className={`mb-3 px-1 text-xs font-semibold uppercase tracking-wider ${isLightTheme ? 'text-gray-600' : 'text-gray-300'}`}>
            Filter by Tag
          </h3>
          <div className="flex flex-wrap max-h-40 overflow-y-auto">
            {tags.map((tag, index) => (
              <button
                type="button"
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, tag)}
                onContextMenu={(e) => handleTagRightClick(e, tag)}
                className={`bg-gray-200 px-2 py-1 rounded-full text-xs mr-2 mb-2 cursor-pointer hover:bg-gray-300 ${
                  selectedTag?.includes(tag) ? 'ring-2 ring-yellow-500 bg-gray-300' : ''
                } text-gray-700`}
                onClick={() => selectTag(tag)}
              >
                <FontAwesomeIcon icon={faTag} className="mr-1" />
                {tag}
              </button>
            ))}
          </div>
          {selectedTag?.length > 0 && (
            <button
              onClick={() => selectTag(null)}
              className="mt-2 block w-full p-2 rounded bg-red-600 text-white hover:bg-red-500"
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Context Menu */}
        {contextMenu.visible && (
          <ul
            ref={contextMenuRef}
            className="fixed bg-white shadow-lg rounded-md py-2 z-50"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <li>
              <button
                type="button"
                disabled={isEncryptionLocked}
                className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleTagDelete(contextMenu.tag)}
              >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Delete Tag
              </button>
            </li>
          </ul>
        )}

        <div className={`mt-5 space-y-2 border-t pt-4 ${isLightTheme ? 'border-gray-400/30' : 'border-blue-950/30'}`}>
          <button
            onClick={() => setShowTagManager(true)}
            className={`block w-full rounded border px-3 py-2 text-sm font-medium ${
              isLightTheme
                ? 'border-blue-300 bg-white/20 text-gray-700 hover:bg-white/40'
                : 'border-blue-300/40 bg-white/5 text-gray-200 hover:bg-white/10'
            }`}
          >
            Manage Tags
          </button>
          <button
            onClick={async () => {
              if (!user) return;
              
              const confirmed = window.confirm(
                'This will restore all categories found in your notes. ' +
                'Existing categories will be preserved if they match. Continue?'
              );
              
              if (!confirmed) return;
              
              setIsRestoring(true);
              try {
                const restoredCategories = await restoreCategoriesFromNotes(user.uid);
                setCategories(restoredCategories);
                alert(`Successfully restored ${restoredCategories.length} categories from your notes!`);
              } catch (error) {
                console.error('Error restoring categories:', error);
                alert('Failed to restore categories. Please try again.');
              } finally {
                setIsRestoring(false);
              }
            }}
            disabled={isRestoring || !user}
            className={`block w-full rounded border px-3 py-2 text-sm font-medium ${
              isRestoring || !user
                ? 'border-gray-400 bg-gray-400 cursor-not-allowed text-white'
                : isLightTheme
                ? 'border-green-300 bg-white/20 text-gray-700 hover:bg-white/40'
                : 'border-green-300/40 bg-white/5 text-gray-200 hover:bg-white/10'
            }`}
            title={!user ? 'Please sign in to restore categories' : 'Restore categories from your notes'}
          >
            {isRestoring ? 'Restoring...' : 'Restore Categories'}
          </button>
        </div>
      </nav>

      {/* Archived Notes Section */}
      <div className={`mt-auto shrink-0 border-t pt-4 ${isLightTheme ? 'border-gray-400/30' : 'border-blue-950/30'}`}>
        <h3 className={`mb-2 flex items-center px-1 text-xs font-semibold uppercase tracking-wider ${isLightTheme ? 'text-gray-600' : 'text-gray-300'}`}>
          <FontAwesomeIcon icon={faBoxArchive} className="mr-2" />
          Archived Notes
        </h3>
        <button
          type="button"
          className={`w-full rounded px-2 py-2 text-left text-sm hover:bg-white/10 hover:text-white cursor-pointer ${
            isLightTheme ? 'text-gray-700' : 'text-gray-300'
          }`}
          onClick={() => selectCategory('Archived')}
        >
          View Archived Notes
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
